import type { BarMetric, BooleanMetric, Metric } from '@vizit/molecules';

/**
 * Helper to determine if a value should be treated as optimal
 * Includes special case: when optimal band extends to range.max and value exceeds it,
 * treat as optimal since we can't know if higher is better without more reference data
 */
function isValueOptimal(metric: BarMetric, value: number): boolean {
  const { optimalBand, range } = metric;
  const bandMin = Math.min(...optimalBand);
  const bandMax = Math.max(...optimalBand);

  // Normal optimal check
  const isInOptimalBand = value >= bandMin && value <= bandMax;

  // Special case: optimal band extends to range.max and value exceeds it
  const tolerance = (range.max - range.min) * 0.05;
  const bandExtendsToMax = Math.abs(bandMax - range.max) < tolerance;
  const valueExceedsRange = value > range.max;
  const treatAsOptimalEdgeCase = bandExtendsToMax && valueExceedsRange;

  return isInOptimalBand || treatAsOptimalEdgeCase;
}

/**
 * Generates description for visual diversity metric
 */
function getVisualDiversityDescription(metric: BarMetric): string {
  const { optimalBand, currentValue } = metric;
  const bandMin = Math.min(...optimalBand);

  if (isValueOptimal(metric, currentValue)) {
    return 'Great visual mix!';
  }

  if (currentValue < bandMin) {
    return 'Imagery is too similar';
  }

  return 'Imagery is too diverse';
}

/**
 * Generates description for asset count metric
 */
function getAssetCountDescription(metric: BarMetric): string {
  const { optimalBand, currentValue } = metric;
  const roundedValue = Math.round(currentValue);

  // Check if optimalBand contains integers (discrete values)
  const isDiscrete = optimalBand.every((v) => Number.isInteger(v));

  if (isDiscrete) {
    const isInOptimal = isValueOptimal(metric, roundedValue);

    if (isInOptimal) {
      return `${roundedValue} is great!`;
    }

    if (roundedValue < Math.min(...optimalBand)) {
      return `${roundedValue} is too low`;
    }

    return `${roundedValue} is too many`;
  }

  // Continuous range - use helper to check optimal including edge case
  const bandMin = Math.min(...optimalBand);

  if (isValueOptimal(metric, currentValue)) {
    return `${roundedValue} is great!`;
  }

  if (currentValue < bandMin) {
    return `${roundedValue} is too low`;
  }

  return `${roundedValue} is too many`;
}

/**
 * Generates description for asset order metric (boolean)
 */
function getAssetOrderDescription(metric: BooleanMetric): string {
  const { currentValue } = metric;

  if (currentValue) {
    return 'Assets are in order!';
  }

  return 'Assets are out of order';
}

/**
 * Generates a dynamic description for any metric based on its key and data
 */
export function getMetricDescription(metric: Metric): string {
  // Handle specific metrics by key
  switch (metric.key) {
    case 'visual_diversity':
      return getVisualDiversityDescription(metric as BarMetric);
    case 'n_carousel_images':
      return getAssetCountDescription(metric as BarMetric);
    case 'order_score':
      return getAssetOrderDescription(metric as BooleanMetric);
    case 'avg_carousel_score':
      return '';
    default:
      // Fallback to type-based descriptions
      switch (metric.type) {
        case 'area':
          return '';
        case 'bar':
          return getAssetCountDescription(metric as BarMetric);
        case 'boolean':
          return getAssetOrderDescription(metric as BooleanMetric);
        default:
          return '';
      }
  }
}
